import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const SamplePick = () => {
  const [sampleStock, setSampleStock] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSamplePick = async () => {
      try {
        // Dynamic API URL detection (Updated: 2025-12-31)
        const getApiUrl = () => {
          if (process.env.REACT_APP_API_URL) {
            return process.env.REACT_APP_API_URL;
          }
          
          const hostname = window.location.hostname;
          const protocol = window.location.protocol;
          
          if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return 'http://localhost:3000';
          } else {
            // Use same protocol and no port (nginx proxy handles routing)
            return `${protocol}//${hostname}`;
          }
        };

        const response = await fetch(`${getApiUrl()}/api/stocks/sample-pick?cache=${Date.now()}`);
        const data = await response.json();
        setSampleStock(data);
      } catch (error) {
        console.error('Error fetching sample pick:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSamplePick();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!sampleStock) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to load sample pick</h2>
          <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Sample AI Stock Pick
            </h1>
            <p className="text-lg text-gray-600">
              See how our AI identified this winning stock before its breakout
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {sampleStock.symbol} - {sampleStock.company}
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pick Date:</span>
                    <span className="font-semibold">{sampleStock.pickDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sell Date:</span>
                    <span className="font-semibold">{sampleStock.sellDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Entry Price:</span>
                    <span className="font-semibold">${sampleStock.pickPrice}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Exit Price:</span>
                    <span className="font-semibold">${sampleStock.sellPrice}</span>
                  </div>
                  <div className="flex justify-between border-t pt-4">
                    <span className="text-gray-600 font-semibold">Total Return:</span>
                    <span className="text-2xl font-bold text-green-600">+{sampleStock.return}%</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4">AI Analysis</h3>
                <p className="text-gray-700 mb-6">{sampleStock.analysis}</p>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Key Signals Detected:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {sampleStock.signals?.map((signal, index) => (
                      <li key={index}>• {signal}</li>
                    ))}
                  </ul>
                </div>

                {sampleStock.disclaimer && (
                  <p className="text-xs text-gray-500 mt-4 italic">
                    {sampleStock.disclaimer}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">
                Get Access to Current AI Picks
              </h3>
              <p className="text-lg mb-6">
                Join thousands of investors getting our latest AI-identified opportunities
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  Start Free Trial
                </Link>
                <Link to="/pricing" className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors">
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SamplePick;
